import type {
    ProfessionalInitialResult,
    ProfessionalAssociation,
} from '@modules/professional/hook/use-professional-initial-route-data';
import { formatProjectValueRange } from './project-value';

/**
 * Trust / social-proof signals for the professional header, split into four
 * shapes: quantitative **stats** (the metric row), a full-width **typicalJob**
 * line (a service pro's budget band, which can run long), an **associations**
 * credential (industry-body memberships), and qualitative **badges**
 * (verification, awards, what the pro offers). Everything is ordered
 * strongest-first for the target audience — a homeowner deciding who to hire, or
 * a specifier vetting a supplier.
 *
 * Derived from the shared initial route data, so it renders the same on every
 * tab. The metric pool is deep and threshold-gated so the row keeps THREE strong
 * numbers even when a signal is thin — decision-relevant fallbacks (years in
 * business, testimonials, network, articles) rather than vanity reach numbers.
 * The type-specific pieces sit behind a single switch ({@link getProfileType})
 * so new per-category variants are cheap.
 */

export type TrustBadgeIcon =
    | 'verified'
    | 'award'
    | 'available'
    | 'specs'
    | 'samples'
    | 'locations';

export interface TrustBadge {
    id: string;
    label: string;
    /** Optional muted second part, e.g. "Since 2014" / "15 awards". */
    detail?: string;
    icon: TrustBadgeIcon;
}

export interface TrustStat {
    id: string;
    value: string;
    label: string;
    /** Render the label before the value, e.g. "Since 1985" instead of "1985 Since". */
    labelFirst?: boolean;
    /** When set, the whole cell links here (e.g. the tab it counts). */
    link?: string;
}

/** Industry-body memberships — a one-line credential plus the raw bodies (with
 *  logos) so the header can switch to a logo strip without new data. */
export interface TrustAssociations {
    /** One-line credential, e.g. "Member of NZ Green Building Council +2". */
    label: string;
    items: ProfessionalAssociation[];
}

export interface ProfessionalTrustSignals {
    badges: TrustBadge[];
    stats: TrustStat[];
    /** Service pro budget band, rendered as its own line so it can run long. */
    typicalJob: string | null;
    /** Industry-association / certification memberships, or null when none. */
    associations: TrustAssociations | null;
}

/** A metric only "counts" as social proof once it clears its floor. */
const SAVES_FLOOR = 25;
/** Mirrors the prod KPI display (`PublicKPI`): show views only above 100. */
const VIEWS_FLOOR = 100;
/** Beyond this many years, a count ("164 yrs") looks like a bug — show the
 *  founding year as heritage ("Since 1862") instead. */
const YEARS_AS_COUNT_MAX = 15;
const TESTIMONIALS_FLOOR = 2;
const NETWORK_FLOOR = 3;
const ARTICLES_FLOOR = 3;
/** Guards against a bogus YearFounded (typos, placeholder years). */
const EARLIEST_FOUNDING_YEAR = 1800;

/** Compact count, e.g. 1487 → "1.5k", 612000 → "612k", 2500000 → "2.5m". */
const formatCount = (count: number): string => {
    if (count >= 1_000_000)
        return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
    if (count >= 1000)
        return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(count);
};

/** Four-digit year the profile went live, or null when unset/unparseable. */
const liveYear = (
    profileLiveDate: string | null | undefined,
): number | null => {
    if (!profileLiveDate) return null;
    const year = new Date(profileLiveDate).getFullYear();
    return Number.isNaN(year) ? null : year;
};

/**
 * The trust "profile type" — the single switch for type-specific signals. Split
 * this further (e.g. architect vs builder off `PrimaryCategory`) when a variant
 * needs its own signals.
 */
type ProfileType = 'supplier' | 'service';

const getProfileType = (pro: TrustSource): ProfileType =>
    pro.IsManufacturer ? 'supplier' : 'service';

type TrustSource = ProfessionalInitialResult;

/** Verification + credibility badges — shared by every professional type. */
const buildBaseBadges = (pro: TrustSource): TrustBadge[] => {
    const badges: TrustBadge[] = [];

    // Platform verification + tenure — the clearest legitimacy signal. The
    // "Since {year}" carries the platform tenure; years-in-business is a
    // separate, stronger experience metric in the row.
    const year = liveYear(pro.ProfileLiveDate);
    if (year) {
        badges.push({
            id: 'verified',
            icon: 'verified',
            label: 'Verified Member',
            detail: `Since ${year}`,
        });
    }

    // Third-party endorsement.
    const awardCount = pro.Awards?.edges?.length ?? 0;
    if (awardCount > 0) {
        badges.push({
            id: 'award',
            icon: 'award',
            label: 'Award-winning',
            detail: awardCount === 1 ? undefined : `${awardCount} awards`,
        });
    }

    return badges;
};

/** Suppliers: whether they offer orderable samples on ArchiPro. */
const buildSupplierBadges = (pro: TrustSource): TrustBadge[] => {
    const badges: TrustBadge[] = [];
    if (pro.SamplesAvailable) {
        badges.push({
            id: 'samples',
            icon: 'samples',
            label: 'Samples available to order on ArchiPro',
        });
    }
    return badges;
};

/** Service pros: currently taking on work. */
const buildServiceBadges = (pro: TrustSource): TrustBadge[] =>
    pro.AvailableForWork
        ? [{ id: 'available', icon: 'available', label: 'Available for work' }]
        : [];

/** Industry-association / certification memberships — the strongest "is this
 *  legit" signal for a homeowner; the body names carry the authority. */
const buildAssociations = (pro: TrustSource): TrustAssociations | null => {
    const items = (pro.Associations ?? []).filter((a) => a.Title);
    const [first] = items;
    const count = pro.AssociationCount || items.length;
    if (!first || count === 0) return null;
    const extra = count - 1;
    const label =
        extra > 0
            ? `Member of ${first.Title} +${extra}`
            : `Member of ${first.Title}`;
    return { label, items };
};

/** Profile views — mirrors the prod KPI display (`PublicKPI`): page views plus
 *  impressions, a large "reach" number that reads as popularity, so it leads the
 *  row (a bigger figure builds trust even though it's not decision-specific). */
const buildViews = (pro: TrustSource): TrustStat | null => {
    const kpi = pro.AggregatedPublicKPI;
    const views = (kpi?.TotalPageViews ?? 0) + (kpi?.TotalImpressions ?? 0);
    return views > VIEWS_FLOOR
        ? { id: 'views', value: formatCount(views), label: 'Views' }
        : null;
};

/** Saves — the crowd-validation lead, once it's a meaningful number. */
const buildSaves = (pro: TrustSource): TrustStat | null => {
    const saves = pro.AggregatedPublicKPI?.TotalSaves ?? 0;
    return saves >= SAVES_FLOOR
        ? { id: 'saves', value: formatCount(saves), label: 'Saves' }
        : null;
};

/** Lead count — products for suppliers, projects otherwise; never "0". */
const buildLeadCount = (
    pro: TrustSource,
    tabBase: string,
): TrustStat | null => {
    if (pro.IsManufacturer && pro.ProductCount > 0) {
        return {
            id: 'count',
            value: formatCount(pro.ProductCount),
            label: 'Products',
            link: `${tabBase}/our-products`,
        };
    }
    if (pro.ProjectCount > 0) {
        return {
            id: 'count',
            value: formatCount(pro.ProjectCount),
            label: 'Projects',
            // The professional Projects tab lives at /case-studies (getSubRoutes).
            link: `${tabBase}/case-studies`,
        };
    }
    if (pro.ProductCount > 0) {
        return {
            id: 'count',
            value: formatCount(pro.ProductCount),
            label: 'Products',
            link: `${tabBase}/our-products`,
        };
    }
    return null;
};

/** Years in business from YearFounded — "in the game N years", the experience
 *  lead and a far stronger tenure signal than platform age. */
const buildYearsInBusiness = (pro: TrustSource): TrustStat | null => {
    if (!pro.YearFounded) return null;
    const founded = Number.parseInt(pro.YearFounded, 10);
    const currentYear = new Date().getFullYear();
    if (
        Number.isNaN(founded) ||
        founded < EARLIEST_FOUNDING_YEAR ||
        founded > currentYear
    ) {
        return null;
    }
    const years = currentYear - founded;
    if (years < 1) return null;
    // Fresh firms show the count ("12 yrs" · In business); long-established ones
    // flip to a "Since 1862" heritage stat so the number never reads as a glitch.
    return years > YEARS_AS_COUNT_MAX
        ? {
              id: 'years-in-business',
              value: `${founded}`,
              label: 'Since',
              labelFirst: true,
          }
        : {
              id: 'years-in-business',
              value: `${years} yrs`,
              label: 'In business',
          };
};

/** Client testimonials — real client words, the closest thing to reviews. */
const buildTestimonials = (pro: TrustSource): TrustStat | null => {
    const count = pro.TestimonialCount ?? 0;
    return count >= TESTIMONIALS_FLOOR
        ? {
              id: 'testimonials',
              value: formatCount(count),
              label: 'Testimonials',
          }
        : null;
};

/** Network size — the pros they collaborate with ("works with N professionals"). */
const buildNetwork = (pro: TrustSource): TrustStat | null => {
    const count = pro.NetworkCount ?? 0;
    return count >= NETWORK_FLOOR
        ? { id: 'network', value: formatCount(count), label: 'Network' }
        : null;
};

/** Published articles — content authority. */
const buildArticles = (pro: TrustSource): TrustStat | null => {
    const count = pro.ArticleCount ?? 0;
    return count >= ARTICLES_FLOOR
        ? { id: 'articles', value: formatCount(count), label: 'Articles' }
        : null;
};

/** Years on ArchiPro — the last-resort tenure fill when nothing stronger fits. */
const buildPlatformYears = (pro: TrustSource): TrustStat | null => {
    const year = liveYear(pro.ProfileLiveDate);
    if (!year) return null;
    const years = Math.max(0, new Date().getFullYear() - year);
    return years >= 1
        ? { id: 'platform-years', value: `${years} yrs`, label: 'On ArchiPro' }
        : null;
};

const compact = <T>(items: (T | null)[]): T[] =>
    items.filter((item): item is T => item !== null);

/** The phone's one line under the ways in: verified since, views, saves. */
export interface ProfessionalVerifiedLine {
    /** The year the profile went live, when it has. */
    verifiedSince: number | null;
    views: TrustStat | null;
    saves: TrustStat | null;
}

/**
 * What the phone's identity block says in one centred line: "ArchiPro
 * Verified since 2014 · 27.6k Views · 639 Saves", as much of it as the
 * profile has.
 */
export const buildProfessionalVerifiedLine = (
    pro: TrustSource,
): ProfessionalVerifiedLine => ({
    verifiedSince: liveYear(pro.ProfileLiveDate),
    views: buildViews(pro),
    saves: buildSaves(pro),
});

export const buildProfessionalTrustSignals = (
    pro: TrustSource,
    /**
     * Where the stat links point. `pro.BaseUrl` is always the public path, so a
     * caller rendering inside the Business Manager preview passes its own tab
     * base — otherwise the figures walk the author's preview onto the live site.
     * Defaults to the public path for callers that only ever render there.
     */
    tabBase: string = pro.BaseUrl,
): ProfessionalTrustSignals => {
    const type = getProfileType(pro);

    const badges = [
        ...buildBaseBadges(pro),
        ...(type === 'supplier'
            ? buildSupplierBadges(pro)
            : buildServiceBadges(pro)),
    ];

    // Three quick-validation metrics, strongest first: reach (Views — a big
    // number that reads as popularity), crowd (Saves), scale (the count),
    // experience (Years in business), then decision-relevant fallbacks
    // (Testimonials, Network, Articles) so a thinner profile still reaches three.
    // Enquiries stay out (a sub-100% "reply rate" reads as a negative). Platform
    // tenure is the final fill, and only when years-in-business is absent, so we
    // never show two "years" metrics side by side.
    const yearsInBusiness = buildYearsInBusiness(pro);
    const stats = compact([
        buildViews(pro),
        buildSaves(pro),
        buildLeadCount(pro, tabBase),
        yearsInBusiness,
        buildTestimonials(pro),
        buildNetwork(pro),
        buildArticles(pro),
        yearsInBusiness ? null : buildPlatformYears(pro),
    ]).slice(0, 3);

    // Typical job band sits on its own line (it can run long) — service pros
    // only; suppliers price at the product level.
    const typicalJob =
        type === 'service' ? formatProjectValueRange(pro.PriceRanges) : null;

    return {
        badges,
        stats,
        typicalJob: typicalJob || null,
        associations: buildAssociations(pro),
    };
};
